/**
 * =============================================================================
 * IDS CRM Web - Opportunity Detail Page
 * =============================================================================
 *
 * PURPOSE:
 * Displays opportunity details with stage switcher, timeline, and watchlist.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import LogCommercialActivityModal from '../components/LogCommercialActivityModal';
import TemplateCheckFormRenderer from '../components/TemplateCheckFormRenderer';
import TimelineEntryEditModal from '../components/TimelineEntryEditModal';
import AuditLogPanel from '../components/AuditLogPanel';
import MarkLostModal from '../components/MarkLostModal';
import ScheduleEntryModal from '../components/ScheduleEntryModal';
import StageChangeDialog from '../components/StageChangeDialog';
import VariationModal from '../components/VariationModal';
import { useToast } from '../components/Toast';
import { OPPORTUNITY_AUDIT_FIELD_LABELS } from '../utils/auditFieldLabels';
import { normaliseAssetConfig } from '../utils/assetTypes';
import { formatDateKey } from '../utils/dates';
import { formatMoney } from '../utils/money';
import { canManageDeals, canMoveDealStage } from '../utils/permissions';
import { stagesForLine } from '../utils/orders';
import './OpportunityDetail.css';
import './CommercialLayout.css';

function OpportunityDetail() {
  const { id } = useParams();
  const { authFetch, user } = useAuth();
  const canManage = canManageDeals(user?.role);
  const canMoveStage = canMoveDealStage(user?.role);

  const [opportunity, setOpportunity] = useState(null);
  const [stages, setStages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [monitorHistory, setMonitorHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [changingStage, setChangingStage] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableGroups, setAssignableGroups] = useState([]);
  const [reassignType, setReassignType] = useState('user');
  const [reassignTo, setReassignTo] = useState('');
  const [reassignNote, setReassignNote] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [reassignError, setReassignError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingTimelineEntry, setEditingTimelineEntry] = useState(null);
  const [editingProbability, setEditingProbability] = useState(false);
  const [probabilityValue, setProbabilityValue] = useState('');
  const [probabilityError, setProbabilityError] = useState(null);
  // Phase R1: pricing is locked at won — a contract variation is the only change path
  const [showVariationModal, setShowVariationModal] = useState(false);
  const { toastElement, showToast } = useToast();
  // Phase 9: whether this product type mints an asset at won (config-driven,
  // never a hardcoded product list) + the asset linked to this line item.
  const [generatesAsset, setGeneratesAsset] = useState(false);
  const [linkedAsset, setLinkedAsset] = useState(null);
  const [showLostModal, setShowLostModal] = useState(false);
  const [checkingAsset, setCheckingAsset] = useState(false);
  // Phase 12: the accountability dialog fronts every stage change (and
  // absorbed the old won-confirm)
  const [stageDialog, setStageDialog] = useState(null);
  const [stageDialogSubmitting, setStageDialogSubmitting] = useState(false);
  const [stageDialogError, setStageDialogError] = useState(null);

  const fetchOpportunity = useCallback(async () => {
    try {
      const [oppRes, stagesRes] = await Promise.all([
        authFetch(`/api/v1/opportunities/${id}`),
        authFetch('/api/v1/opportunities/pipeline-stages')
      ]);

      if (!oppRes.ok) {
        throw new Error('Opportunity not found');
      }

      const oppData = await oppRes.json();
      const stagesData = await stagesRes.json();

      setOpportunity(oppData);
      // An order shows only the stages orders use (Product Configuration).
      setStages(stagesForLine(stagesData.data?.[oppData.product] || [], oppData));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, authFetch]);

  const fetchTimeline = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/timeline?opportunity_id=${id}&limit=50`);
      const data = await response.json();
      setTimeline(data.data || []);
    } catch (err) {
      console.error('Failed to fetch timeline:', err);
    }
  }, [id, authFetch]);

  const fetchMonitorHistory = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/opportunities/${id}/history`);
      const data = await response.json();
      setMonitorHistory(data.data || []);
    } catch (err) {
      console.error('Failed to fetch monitor history:', err);
    }
  }, [id, authFetch]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  useEffect(() => {
    if (!opportunity?.product) return;

    let cancelled = false;
    setCheckingAsset(true);
    Promise.all([
      authFetch('/api/v1/assets/config').then(response => response.json()),
      opportunity.won_at
        ? authFetch(`/api/v1/assets?linked_opportunity_id=${id}&limit=1`).then(response => response.json())
        : Promise.resolve({ data: [] })
    ])
      .then(([configData, assetData]) => {
        if (cancelled) return;
        const typeConfig = normaliseAssetConfig(configData.data).asset_types
          .find(type => type.value === opportunity.product);
        setGeneratesAsset(Boolean(typeConfig?.generates_asset));
        setLinkedAsset((assetData.data || [])[0] || null);
      })
      .catch(() => {
        if (!cancelled) {
          setGeneratesAsset(false);
          setLinkedAsset(null);
        }
      })
      .finally(() => {
        if (!cancelled) setCheckingAsset(false);
      });

    return () => { cancelled = true; };
  }, [id, authFetch, opportunity?.won_at, opportunity?.product]);

  useEffect(() => {
    if (activeTab === 'timeline') {
      fetchTimeline();
    } else if (activeTab === 'watchlist') {
      fetchMonitorHistory();
    }
  }, [activeTab, fetchTimeline, fetchMonitorHistory]);

  const [stageError, setStageError] = useState(null);
  // Phase 10: 400 SCHEDULE_ENTRY_REQUIRED raises the schedule modal
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const applyStageChange = async (newStage, extras = {}) => {
    const { stageChange = null, scheduleEntry = null } = extras;
    setChangingStage(true);
    setStageError(null);
    if (stageChange && !scheduleEntry) setStageDialogSubmitting(true);
    if (scheduleEntry) setScheduleSubmitting(true);
    try {
      const response = await authFetch(`/api/v1/opportunities/${id}/stage`, {
        method: 'PUT',
        body: JSON.stringify({
          stage: newStage,
          ...(stageChange ? { stage_change: { ...stageChange, source: 'opportunity_detail' } } : {}),
          ...(scheduleEntry ? { schedule_entry: scheduleEntry } : {})
        })
      });

      if (response.ok) {
        setStageDialog(null);
        setStageDialogError(null);
        setScheduleModal(null);
        setScheduleError(null);
        await fetchOpportunity();
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'STAGE_CHANGE_DETAILS_REQUIRED') {
          if (stageChange) {
            setStageDialogError(data.message);
          } else {
            setStageDialogError(null);
            setStageDialog({ stageName: newStage, details: data.details || {} });
          }
          return;
        }
        if (data.code === 'SCHEDULE_ENTRY_REQUIRED') {
          setStageDialog(null);
          if (scheduleEntry) {
            setScheduleError(data.message);
          } else {
            setScheduleError(null);
            setScheduleModal({ stageName: newStage, demand: data.details || {}, stageChange });
          }
          return;
        }
        setStageError(data.message || `Stage change failed (HTTP ${response.status})`);
      }
    } catch (err) {
      console.error('Failed to change stage:', err);
      setStageError('Stage change failed - check your connection and try again.');
    } finally {
      setChangingStage(false);
      setStageDialogSubmitting(false);
      setScheduleSubmitting(false);
    }
  };

  const handleStageChange = (newStage) => {
    applyStageChange(newStage);
  };

  const openReassign = async () => {
    setReassignError(null);
    setReassignNote('');
    setReassignTo('');
    setReassignType('user');
    setShowReassignModal(true);
    try {
      const [usersRes, groupsRes] = await Promise.all([
        authFetch('/api/v1/opportunities/assignable-users'),
        authFetch('/api/v1/groups?purpose=operational')
      ]);
      if (usersRes.ok) {
        const data = await usersRes.json();
        setAssignableUsers(data.data || []);
      }
      if (groupsRes.ok) {
        const data = await groupsRes.json();
        setAssignableGroups(data.data || []);
      }
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    }
  };

  const handleReassign = async () => {
    if (!reassignTo) {
      setReassignError(reassignType === 'group' ? 'Select a group' : 'Select a team member');
      return;
    }
    setReassigning(true);
    setReassignError(null);
    try {
      const res = await authFetch(`/api/v1/opportunities/${id}/assign`, {
        method: 'PUT',
        body: JSON.stringify({
          ...(reassignType === 'group' ? { owner_group_id: reassignTo } : { owner_id: reassignTo }),
          note: reassignNote || undefined
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setOpportunity(prev => ({ ...prev, ...updated }));
        setShowReassignModal(false);
        fetchOpportunity();
      } else {
        const data = await res.json().catch(() => ({}));
        setReassignError(data.message || 'Failed to reassign');
      }
    } catch (err) {
      setReassignError('Failed to reassign');
    } finally {
      setReassigning(false);
    }
  };

  const handleProbabilityEdit = () => {
    setEditingProbability(true);
    setProbabilityValue(String(opportunity.probability_pct || 0));
    setProbabilityError(null);
  };

  const handleProbabilitySave = async () => {
    const value = parseInt(probabilityValue, 10);
    if (isNaN(value) || value < 0 || value > 100) {
      setProbabilityError('Must be 0-100');
      return;
    }

    try {
      const response = await authFetch(`/api/v1/opportunities/${id}/probability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ probability_pct: value })
      });

      if (response.ok) {
        const updated = await response.json();
        setOpportunity(updated);
        setEditingProbability(false);
        setProbabilityError(null);
      } else {
        const data = await response.json();
        setProbabilityError(data.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Failed to update probability:', err);
      setProbabilityError('Failed to save');
    }
  };

  const handleProbabilityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleProbabilitySave();
    } else if (e.key === 'Escape') {
      setEditingProbability(false);
      setProbabilityError(null);
    }
  };

  const formatCurrency = (minorUnits, currency = 'GBP') => formatMoney(minorUnits, currency);

  // DATE columns (expected_close, revised_expected_close, monitor_review_date,
  // action_due_date) are calendar days. created_at is an instant and stays on
  // the viewer's own day: formatInstantDate.
  const formatDate = (date) => formatDateKey(date);

  const formatInstantDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return <div className="loading-page">Loading opportunity...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/pipeline" className="btn-secondary">← Commercial</BackToList>
      </div>
    );
  }

  const currentStageIndex = stages.findIndex(s => s.stage_name === opportunity.stage);

  return (
    <div className="opportunity-detail">
      {toastElement}
      <div className="detail-header">
        <div className="header-info">
          <BackToList to="/pipeline" className="back-link">← Commercial</BackToList>
          <h1>{opportunity.title}</h1>
          <div className="header-meta">
            <span className="opp-ref">{opportunity.opp_ref}</span>
            {opportunity.is_order && <span className="order-badge">Order</span>}
            <span className="product-badge">{opportunity.product.replace(/_/g, ' ')}</span>
            <span className="value-badge">
              {formatCurrency(opportunity.value_minor_units, opportunity.currency)}
            </span>
            {opportunity.approval_status === 'pending' && (
              <span className="approval-badge pending">Awaiting Approval</span>
            )}
            {opportunity.approval_status === 'rejected' && (
              <span className="approval-badge rejected">Action Required</span>
            )}
            {opportunity.won_at && !opportunity.lost_at && (
              <span className="approval-badge won">Won</span>
            )}
            {opportunity.lost_at && (
              <span className="approval-badge rejected">
                Lost{opportunity.lost_reason ? ` — ${String(opportunity.lost_reason).replace(/_/g, ' ')}` : ''}
              </span>
            )}
            {opportunity.opportunity_group_id && (
              <Link to={`/pipeline/groups/${opportunity.opportunity_group_id}`} className="group-link">
                {opportunity.is_order ? 'View Order' : 'View Opportunity'}
              </Link>
            )}
            {opportunity.is_monitored && (
              <span className={`monitor-badge ${opportunity.monitor_priority}`}>
                Watchlist: {opportunity.monitor_priority}
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          {canManage && !opportunity.won_at && !opportunity.lost_at && !opportunity.cancelled_at && (
            <button type="button" className="btn-danger" onClick={() => setShowLostModal(true)}>Mark Lost</button>
          )}
          {canManage && <button type="button" className="btn-secondary" onClick={openReassign}>Reassign</button>}
          {canManage && <Link to={`/pipeline/${id}/edit`} className="btn-secondary">Edit</Link>}
        </div>
      </div>

      {/* Stage Progress */}
      <div className="stage-progress">
        <div className="stage-track">
          {stages.filter(s => !s.is_closed_lost).map((stage, idx) => {
            const isCurrent = stage.stage_name === opportunity.stage;
            const isPast = idx < currentStageIndex;
            const isClosed = stage.is_closed_won;

            return (
              <button
                key={stage.stage_name}
                className={`stage-step ${isCurrent ? 'current' : ''} ${isPast ? 'completed' : ''} ${isClosed ? 'closed' : ''}`}
                onClick={() => !isCurrent && !changingStage && handleStageChange(stage.stage_name)}
                disabled={!canMoveStage || changingStage || opportunity.approval_status === 'pending' || opportunity.approval_status === 'rejected'}
                title={opportunity.approval_status === 'pending'
                  ? 'Awaiting approval — cannot progress yet'
                  : opportunity.approval_status === 'rejected'
                    ? 'Rejected — revise the pricing first'
                    : stage.stage_name}
              >
                <span className="stage-dot" />
                <span className="stage-label">{stage.stage_ref}</span>
              </button>
            );
          })}
        </div>
        <div className="current-stage-name">
          Current: <strong>{opportunity.stage}</strong> ({opportunity.probability_pct}% probability)
        </div>
      </div>

      {stageError && (
        <div className="asset-creation-prompt stage-error-banner">
          <span>{stageError}</span>
          <button type="button" className="btn-secondary" onClick={() => setStageError(null)}>Dismiss</button>
        </div>
      )}

      {opportunity.won_at && generatesAsset && !checkingAsset && linkedAsset && (
        <div className="asset-creation-prompt">
          <span>
            {linkedAsset.status === 'pending_install'
              ? 'The asset record was generated at won — complete it (serial number, site, install date) before the install-complete stage.'
              : 'This line item\'s asset record:'}
          </span>
          <Link to={`/assets/${linkedAsset.asset_id}`} className="btn-primary">
            {linkedAsset.asset_ref || 'View Asset'}{linkedAsset.status === 'pending_install' ? ' · Pending Install' : ''}
          </Link>
        </div>
      )}

      {opportunity.won_at && generatesAsset && !checkingAsset && !linkedAsset && (
        <div className="asset-creation-prompt">
          <span>This opportunity is won — create the asset record to start tracking service and maintenance.</span>
          <Link
            to={`/assets/new?customer_id=${opportunity.customer_id}&robot_type=${opportunity.product}&linked_opportunity_id=${id}`}
            className="btn-primary"
          >
            Create Asset
          </Link>
        </div>
      )}

      <div className="detail-tabs">
        <button
          className={activeTab === 'details' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={activeTab === 'timeline' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('timeline')}
        >
          Timeline
        </button>
        <button
          className={activeTab === 'watchlist' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('watchlist')}
        >
          Watchlist History
        </button>
        <button
          className={activeTab === 'history' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      <div className="detail-content">
        {activeTab === 'details' && (
          <div className="details-grid">
            <div className="detail-section">
              <h3>Customer</h3>
              <dl className="info-list">
                <dt>Customer</dt>
                <dd>
                  <Link to={`/customers/${opportunity.customer_id}`}>
                    {opportunity.customer_name}
                  </Link>
                </dd>
                <dt>Primary Contact</dt>
                <dd>{opportunity.primary_contact_name || '-'}</dd>
                <dt>Account Owner</dt>
                <dd>{opportunity.owner_name || (opportunity.owner_group_name ? `${opportunity.owner_group_name} (group)` : '-')}</dd>
                <dt>Territory</dt>
                <dd>{opportunity.territory || '-'}</dd>
              </dl>
            </div>

            <div className="detail-section">
              <h3>Opportunity</h3>
              <dl className="info-list">
                <dt>Product</dt>
                <dd>{(opportunity.product || opportunity.description || '-').replace(/_/g, ' ')}</dd>
                <dt>Sale Channel</dt>
                <dd>{opportunity.sale_channel.replace(/_/g, ' ')}</dd>
                <dt>Source</dt>
                <dd>{opportunity.source || '-'}</dd>
              </dl>
            </div>

            <div className="detail-section">
              <h3>Value & Dates</h3>
              <dl className="info-list">
                <dt>Value</dt>
                <dd>
                  {formatCurrency(opportunity.value_minor_units, opportunity.currency)}
                  {opportunity.won_at && !opportunity.lost_at && !opportunity.cancelled_at && (
                    <span className="pricing-lock-row">
                      <span className="approval-badge won" title="Won — pricing can only change via an approved contract variation">
                        Locked — deposit paid
                      </span>
                      <button type="button" className="btn-small" onClick={() => setShowVariationModal(true)}>
                        Request variation
                      </button>
                    </span>
                  )}
                </dd>
                <dt>Probability</dt>
                <dd>
                  {editingProbability ? (
                    <span className="probability-edit">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={probabilityValue}
                        onChange={(e) => setProbabilityValue(e.target.value)}
                        onKeyDown={handleProbabilityKeyDown}
                        onBlur={handleProbabilitySave}
                        className={probabilityError ? 'error' : ''}
                        autoFocus
                      />%
                      {probabilityError && <span className="prob-error-msg">{probabilityError}</span>}
                    </span>
                  ) : (
                    <span className="probability-editable" onClick={handleProbabilityEdit} title="Click to edit">
                      {opportunity.probability_pct}%
                    </span>
                  )}
                </dd>
                <dt>Weighted Value</dt>
                <dd>{formatCurrency(opportunity.value_minor_units * opportunity.probability_pct / 100, opportunity.currency)}</dd>
                <dt>Expected Close</dt>
                <dd>{formatDate(opportunity.expected_close)}</dd>
                <dt>Created</dt>
                <dd>{formatInstantDate(opportunity.created_at)}</dd>
              </dl>
            </div>

            {opportunity.distributor_id && (
              <div className="detail-section">
                <h3>Distributor</h3>
                <dl className="info-list">
                  <dt>Distributor</dt>
                  <dd>{opportunity.distributor_name}</dd>
                  <dt>End Customer</dt>
                  <dd>{opportunity.end_customer_name || '-'}</dd>
                </dl>
              </div>
            )}

            {opportunity.notes && (
              <div className="detail-section full-width">
                <h3>Notes</h3>
                <p className="notes-content">{opportunity.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="timeline-section">
            <div className="list-header">
              <h3>Activity Timeline</h3>
              <button className="btn-small" onClick={() => setShowActivityModal(true)}>+ Log Activity</button>
            </div>
            {timeline.length === 0 ? (
              <div className="empty-message">No activity recorded yet</div>
            ) : (
              <div className="timeline-list">
                {timeline.map(entry => (
                  <div key={entry.entry_id} className="timeline-item">
                    <div className="timeline-icon">{getEntryIcon(entry.entry_type)}</div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="entry-type">{entry.entry_type.replace(/_/g, ' ')}</span>
                        <div className="timeline-actions">
                          <button type="button" className="timeline-edit-btn" onClick={() => setEditingTimelineEntry(entry)}>Edit</button>
                          <span className="entry-date">{formatDateTime(entry.occurred_at)}</span>
                        </div>
                      </div>
                      {entry.subject && <div className="entry-subject">{entry.subject}</div>}
                      {entry.body && <div className="entry-body">{entry.body}</div>}
                      {entry.form_template_id && entry.form_responses?.schema && (
                        <details className="entry-sales-form">
                          <summary>
                            Sales form: {entry.form_responses.template_name || 'Attached form'}
                          </summary>
                          <div className="entry-sales-form-body">
                            <TemplateCheckFormRenderer
                              schema={entry.form_responses.schema}
                              answers={entry.form_responses.answers || {}}
                              stepAttachments={(entry.form_responses.attachments || []).reduce((byStep, item) => {
                                if (item && item.step_key) byStep[item.step_key] = [...(byStep[item.step_key] || []), item];
                                return byStep;
                              }, {})}
                              onAnswersChange={() => {}}
                              disabled
                            />
                          </div>
                        </details>
                      )}
                      <div className="entry-meta">
                        By {entry.user_name}
                        {entry.contact_name && ` with ${entry.contact_name}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'watchlist' && (
          <div className="watchlist-section">
            <div className="list-header">
              <h3>Monitor History (Contracts Meeting)</h3>
              {opportunity.is_monitored && (
                <button className="btn-small" onClick={() => setShowReviewModal(true)}>+ Add Review</button>
              )}
            </div>

            {opportunity.is_monitored && (
              <div className="current-monitor-status">
                <div className={`priority-indicator ${opportunity.monitor_priority}`}>
                  {opportunity.monitor_priority}
                </div>
                <div className="monitor-details">
                  <strong>Reason:</strong> {opportunity.monitor_reason}<br />
                  <strong>Next Review:</strong> {formatDate(opportunity.monitor_review_date)}
                  {opportunity.monitor_next_step && (
                    <><br /><strong>Next step:</strong> {opportunity.monitor_next_step}</>
                  )}
                  <br /><strong>Next-action owner:</strong> {opportunity.monitor_assigned_to_name || '— unassigned —'}
                  <br /><strong>Target close:</strong> {formatDate(opportunity.expected_close)}
                  {opportunity.revised_expected_close && (
                    <><br /><strong>Revised close:</strong> {formatDate(opportunity.revised_expected_close)}
                      {opportunity.revised_close_reason && ` — ${opportunity.revised_close_reason}`}</>
                  )}
                  {opportunity.delay_reason && (
                    <><br /><strong>Delay reason:</strong> {opportunity.delay_reason}</>
                  )}
                  {opportunity.hold_reason && (
                    <><br /><strong>On-hold reason:</strong> {opportunity.hold_reason}</>
                  )}
                  {opportunity.lost_reason && (
                    <><br /><strong>Lost reason:</strong> {opportunity.lost_reason}</>
                  )}
                </div>
              </div>
            )}

            {monitorHistory.length === 0 ? (
              <div className="empty-message">
                {opportunity.is_monitored
                  ? 'No reviews recorded yet'
                  : 'This opportunity is not on the watchlist'}
              </div>
            ) : (
              <div className="history-list">
                {monitorHistory.map(entry => (
                  <div key={entry.history_id} className="history-item">
                    <div className={`priority-dot ${entry.priority_at_review}`} />
                    <div className="history-content">
                      <div className="history-header">
                        <span className="review-date">{formatDateTime(entry.reviewed_at)}</span>
                        <span className="reviewer">by {entry.reviewed_by_name}</span>
                      </div>
                      {entry.status_update && (
                        <div className="status-update">{entry.status_update}</div>
                      )}
                      {entry.narrative && (
                        <div className="narrative">{entry.narrative}</div>
                      )}
                      {entry.action_agreed && (
                        <div className="action-agreed">
                          <strong>Action:</strong> {entry.action_agreed}
                          {entry.action_owner_name && ` (${entry.action_owner_name})`}
                          {entry.action_due_date && ` - Due: ${formatDate(entry.action_due_date)}`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <AuditLogPanel
            fetchUrl={`/api/v1/opportunities/${id}/audit-log`}
            fieldLabels={OPPORTUNITY_AUDIT_FIELD_LABELS}
            emptyMessage="No changes have been recorded for this opportunity."
          />
        )}
      </div>

      {/* Log Activity Modal */}
      {showActivityModal && (
        <LogCommercialActivityModal
          opportunityId={id}
          customerId={opportunity.customer_id}
          customerName={opportunity.customer_name}
          authFetch={authFetch}
          stages={stages}
          currentStage={opportunity.stage}
          onClose={() => setShowActivityModal(false)}
          onSaved={async (activity, progressStage) => {
            setShowActivityModal(false);
            if (progressStage && progressStage !== opportunity.stage) {
              await handleStageChange(progressStage);
            }
            fetchTimeline();
          }}
        />
      )}

      {stageDialog && (
        <StageChangeDialog
          stageName={stageDialog.stageName}
          lineLabel={opportunity.title || opportunity.opp_ref}
          details={stageDialog.details}
          submitting={stageDialogSubmitting}
          submitError={stageDialogError}
          onCancel={() => {
            setStageDialog(null);
            setStageDialogError(null);
          }}
          onSubmit={(stageChange) => applyStageChange(stageDialog.stageName, { stageChange })}
        />
      )}

      {scheduleModal && (
        <ScheduleEntryModal
          stageName={scheduleModal.stageName}
          lineLabel={opportunity.title || opportunity.opp_ref}
          demand={scheduleModal.demand}
          submitting={scheduleSubmitting}
          submitError={scheduleError}
          onCancel={() => {
            setScheduleModal(null);
            setScheduleError(null);
          }}
          onSubmit={(entry) => applyStageChange(scheduleModal.stageName, {
            stageChange: scheduleModal.stageChange,
            scheduleEntry: entry
          })}
        />
      )}

      {/* Contract Variation Modal (Phase R1) */}
      {showVariationModal && (
        <VariationModal
          line={{
            opportunity_id: id,
            opp_ref: opportunity.opp_ref,
            pricing_schedule: opportunity.pricing_schedule,
            currency: opportunity.currency
          }}
          onClose={() => setShowVariationModal(false)}
          onSubmitted={() => {
            setShowVariationModal(false);
            showToast('Variation submitted for approval', 'success');
            fetchOpportunity();
          }}
        />
      )}

      {/* Mark Lost Modal */}
      {showLostModal && (
        <MarkLostModal
          item={opportunity}
          authFetch={authFetch}
          onClose={() => setShowLostModal(false)}
          onDone={() => {
            setShowLostModal(false);
            fetchOpportunity();
          }}
        />
      )}

      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Reassign Opportunity</h2>
              <button type="button" className="modal-close" onClick={() => setShowReassignModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {reassignError && <div className="modal-error">{reassignError}</div>}
              <div className="form-group">
                <label>Assign to *</label>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 400 }}>
                    <input type="radio" checked={reassignType === 'user'} onChange={() => { setReassignType('user'); setReassignTo(''); }} />
                    A person
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 400 }}>
                    <input type="radio" checked={reassignType === 'group'} onChange={() => { setReassignType('group'); setReassignTo(''); }} />
                    A group
                  </label>
                </div>
                {reassignType === 'user' ? (
                  <select id="reassign-to" value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                    <option value="">Select team member...</option>
                    {assignableUsers.map(u => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.full_name}{u.role ? ` (${u.role})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select id="reassign-to" value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                    <option value="">Select group...</option>
                    {assignableGroups.map(g => (
                      <option key={g.group_id} value={g.group_id}>
                        {g.name}{Number(g.member_count) >= 0 ? ` (${g.member_count} members)` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="reassign-note">Note (optional)</label>
                <input
                  id="reassign-note"
                  type="text"
                  value={reassignNote}
                  onChange={e => setReassignNote(e.target.value)}
                  placeholder="Reason for the handoff"
                />
              </div>
              <p className="reassign-hint">The new owner is alerted, and the handoff is recorded on the timeline and history.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowReassignModal(false)}>Cancel</button>
              <button type="button" className="btn-save" onClick={handleReassign} disabled={reassigning}>
                {reassigning ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Review Modal */}
      {showReviewModal && (
        <AddReviewModal
          opportunityId={id}
          authFetch={authFetch}
          onClose={() => setShowReviewModal(false)}
          onSaved={() => {
            setShowReviewModal(false);
            fetchMonitorHistory();
            fetchOpportunity();
          }}
        />
      )}

      {editingTimelineEntry && (
        <TimelineEntryEditModal
          entry={editingTimelineEntry}
          authFetch={authFetch}
          onClose={() => setEditingTimelineEntry(null)}
          onSaved={() => {
            setEditingTimelineEntry(null);
            fetchTimeline();
          }}
        />
      )}
    </div>
  );
}

/**
 * Add Review Modal Component (Contracts Meeting)
 */
function AddReviewModal({ opportunityId, authFetch, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [owners, setOwners] = useState([]);
  const [formData, setFormData] = useState({
    priority_at_review: 'amber',
    status_update: '',
    narrative: '',
    next_review_date: '',
    action_agreed: '',
    action_owner_id: '',
    action_due_date: '',
    revised_expected_close: '',
    revised_close_reason: '',
    delay_reason: '',
    hold_reason: ''
  });

  useEffect(() => {
    (async () => {
      try {
        // Gareth 2026-08-21: a next action agreed at the Contracts Meeting is
        // often a site survey, which goes to an engineer — so this picker
        // lists EVERY active user, not just deal owners (ROLES.DEALS). Deal
        // OWNERSHIP deliberately still uses assignable-users.
        const res = await authFetch('/api/v1/opportunities/schedulable-users');
        if (res.ok) { const data = await res.json(); setOwners(data.data || data || []); }
      } catch (err) { /* non-fatal */ }
    })();
  }, [authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (!formData.next_review_date) {
      setError('Next review date is required');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        priority_at_review: formData.priority_at_review,
        status_update: formData.status_update || null,
        narrative: formData.narrative || null,
        next_review_date: formData.next_review_date,
        action_agreed: formData.action_agreed || null,
        action_owner_id: formData.action_owner_id || null,
        action_due_date: formData.action_due_date || null,
        revised_expected_close: formData.revised_expected_close || null,
        revised_close_reason: formData.revised_close_reason || null,
        delay_reason: formData.delay_reason || null,
        hold_reason: formData.hold_reason || null
      };

      const response = await authFetch(`/api/v1/opportunities/${opportunityId}/monitor`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save review');
      }
    } catch (err) {
      setError('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Contracts Meeting Review</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <div className="modal-body">
            <div className="form-group">
              <label>Priority *</label>
              <div className="priority-buttons">
                <button
                  type="button"
                  className={`priority-btn green ${formData.priority_at_review === 'green' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, priority_at_review: 'green' }))}
                >
                  Green
                </button>
                <button
                  type="button"
                  className={`priority-btn amber ${formData.priority_at_review === 'amber' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, priority_at_review: 'amber' }))}
                >
                  Amber
                </button>
                <button
                  type="button"
                  className={`priority-btn red ${formData.priority_at_review === 'red' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, priority_at_review: 'red' }))}
                >
                  Red
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="status_update">Status Update (Milestone)</label>
              <input
                type="text"
                id="status_update"
                name="status_update"
                value={formData.status_update}
                onChange={handleChange}
                placeholder="e.g., Deposit paid 30/04/26"
              />
            </div>

            <div className="form-group">
              <label htmlFor="narrative">Current Situation</label>
              <textarea
                id="narrative"
                name="narrative"
                value={formData.narrative}
                onChange={handleChange}
                rows={3}
                placeholder="Notes on current state of this opportunity..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="next_review_date">Next Review Date *</label>
              <input
                type="date"
                id="next_review_date"
                name="next_review_date"
                value={formData.next_review_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="action_agreed">Next required step</label>
                <input
                  type="text"
                  id="action_agreed"
                  name="action_agreed"
                  value={formData.action_agreed}
                  onChange={handleChange}
                  placeholder="What is the next step?"
                />
              </div>
              <div className="form-group">
                <label htmlFor="action_owner_id">Next-action owner</label>
                <select id="action_owner_id" name="action_owner_id" value={formData.action_owner_id} onChange={handleChange}>
                  <option value="">— unassigned —</option>
                  {owners.map(o => <option key={o.user_id} value={o.user_id}>{o.full_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="action_due_date">Action due date</label>
                <input
                  type="date"
                  id="action_due_date"
                  name="action_due_date"
                  value={formData.action_due_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="revised_expected_close">Revised expected close</label>
                <input type="date" id="revised_expected_close" name="revised_expected_close"
                  value={formData.revised_expected_close} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="revised_close_reason">Reason for revised date</label>
                <input type="text" id="revised_close_reason" name="revised_close_reason"
                  value={formData.revised_close_reason} onChange={handleChange} placeholder="Why did the date move?" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="delay_reason">Delay reason</label>
                <input type="text" id="delay_reason" name="delay_reason"
                  value={formData.delay_reason} onChange={handleChange} placeholder="Why is this deal behind?" />
              </div>
              <div className="form-group">
                <label htmlFor="hold_reason">On-hold reason</label>
                <input type="text" id="hold_reason" name="hold_reason"
                  value={formData.hold_reason} onChange={handleChange} placeholder="If on hold, why?" />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : 'Save Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getEntryIcon(type) {
  const icons = {
    call: '📞',
    email: '✉️',
    meeting: '👥',
    site_visit: '🚗',
    demo: '🎯',
    note: '📝',
    task: '✓',
    quote_sent: '📄',
    deal_won: '🎉',
    deal_lost: '❌',
    support_job: '🔧',
    order: '📦',
    account_review: '📊'
  };
  return icons[type] || '•';
}

export default OpportunityDetail;
